import { from, of } from 'rxjs';
import { ConfirmBankAccountRequest, ConfirmBankAccountResponse } from '@square/dashboard-protos/models/squareup/deposits/bank_account_service';
import { getLinkedBankAccountLabel } from '@banking/ui/utils/checking/utils';
import { htmlSafe } from '@ember/template';

// State
// =============================================================================

export let Screen = /*#__PURE__*/function (Screen) {
  Screen["ERROR"] = "CONFIRM_BANK_ACCOUNT/ERROR";
  Screen["SUCCESS"] = "CONFIRM_BANK_ACCOUNT/SUCCESS";
  return Screen;
}({});
export const INITIAL_STATE = {
  bankLabel: {
    bankAccountLabelHtmlSafe: htmlSafe(''),
    bankAccountLabel: ''
  },
  isDone: false,
  isModalOpen: false,
  locationName: '',
  screen: Screen.SUCCESS,
  unitAccounts: []
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});

// Action
// =============================================================================

export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["ConfirmBankAccountReceived"] = "CONFIRM_BANK_ACCOUNT_RECEIVED";
  ActionType["ConfirmBankAccountRejected"] = "CONFIRM_BANK_ACCOUNT_REJECTED";
  ActionType["InitConfirmBankAccount"] = "INIT_CONFIRM_BANK_ACCOUNT";
  ActionType["IncrementScreen"] = "CONFIRM_BANK_ACCOUNT_INCREMENT_SCREEN";
  ActionType["ToggleConfirmBankAccountModal"] = "TOGGLE_CONFIRM_BANK_ACCOUNT_MODAL";
  return ActionType;
}({});
// Action Creators
// =============================================================================

export const confirmBankAccountRejectedAction = error => ({
  type: ActionType.ConfirmBankAccountRejected,
  error
});
export const confirmBankAccountReceivedAction = response => ({
  type: ActionType.ConfirmBankAccountReceived,
  response
});
export const incrementScreenAction = screen => ({
  type: ActionType.IncrementScreen,
  screen
});
export const initConfirmBankAccountAction = (confirmationToken, unitAccounts) => ({
  type: ActionType.InitConfirmBankAccount,
  confirmationToken,
  unitAccounts
});
export const toggleConfirmBankAccountModalAction = () => ({
  type: ActionType.ToggleConfirmBankAccountModal
});

// Update
// =============================================================================

function handleInitConfirmBankAccount(context, state, action) {
  const {
    confirmationToken,
    unitAccounts
  } = action;
  const work = () => {
    const confirmBankAccount = context.bankAccountApi.confirmBankAccount(ConfirmBankAccountRequest.create({
      confirmationToken
    })).then(confirmBankAccountReceivedAction, confirmBankAccountRejectedAction);
    return from(confirmBankAccount);
  };
  return [{
    ...state,
    unitAccounts
  }, work];
}
function handleConfirmBankAccountRejected(context, state) {
  context.sentry.captureException('ConfirmBankAccount failed!');
  const work = () => {
    return of(incrementScreenAction(Screen.ERROR), toggleConfirmBankAccountModalAction());
  };
  return [{
    ...state
  }, work];
}
function handleConfirmBankAccountReceived(context, state, action) {
  const {
    response
  } = action;
  const {
    unitAccounts
  } = state;
  const {
    status,
    confirmedBankAccount,
    bankAccountOwner
  } = response;
  let nextScreenAction = incrementScreenAction(Screen.SUCCESS);
  let nextState = state;
  if (status === ConfirmBankAccountResponse.ConfirmBankAccountStatus.CONFIRMATION_TOKEN_INVALID) {
    context.sentry.captureException('ConfirmBankAccount failed!');
    nextScreenAction = incrementScreenAction(Screen.ERROR);
  }
  if (confirmedBankAccount && bankAccountOwner) {
    const locationName = unitAccounts.find(account => bankAccountOwner.id === account.owner?.id)?.name ?? '';
    const bankLabel = getLinkedBankAccountLabel(confirmedBankAccount, context.intl);
    nextState = {
      ...state,
      locationName,
      bankLabel
    };
  }
  const work = () => {
    return of(nextScreenAction, toggleConfirmBankAccountModalAction());
  };
  return [nextState, work];
}
function handleIncrementScreen(state, action) {
  const {
    screen
  } = action;
  return [{
    ...state,
    screen
  }, null];
}
function handleToggleModal(state) {
  const {
    isModalOpen
  } = state;
  if (isModalOpen === false) {
    return [{
      ...state,
      isModalOpen: true
    }, null];
  }

  // reset state on modal close
  return [{
    ...INITIAL_STATE,
    isDone: true
  }, null];
}
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.IncrementScreen:
      return handleIncrementScreen(state, action);
    case ActionType.InitConfirmBankAccount:
      return handleInitConfirmBankAccount(context, state, action);
    case ActionType.ConfirmBankAccountRejected:
      return handleConfirmBankAccountRejected(context, state);
    case ActionType.ConfirmBankAccountReceived:
      return handleConfirmBankAccountReceived(context, state, action);
    case ActionType.ToggleConfirmBankAccountModal:
      return handleToggleModal(state);
  }
};